import { RecoilRoot } from 'recoil';

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import Loader from 'react-loader-spinner';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import './_assets/base.scss';
import 'react-toastify/dist/ReactToastify.css';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { OfflineProvider } from '@aldridge/aldg-helpers';
import Header from './_GlobalComponents/Header';
import Footer from './_GlobalComponents/Footer';
import VersionTag from './_GlobalComponents/VersionTag';
import Application from './Pages/Application';
import UserProvider from './providers/UserProvider';
import ServiceWorkerWrapper from './ServiceWorkerWrapper';
import { config } from './firebase';
// import ChangelogModal from './ChangelogModal';
import ErrorBoundary from './utils/ErrorBoundary';
import { AldgTheme } from './theme';

// Set by ci-develop.yml / ci-release.yml at build time from `git describe --tags --always`:
// "v1.7.0" on a release, "v1.7.0-3-g618a7ac" on dev (3 commits past the tag), or the commit
// alone before any release tag exists. Local builds have no value.
const BUILD_ID = process.env.REACT_APP_BUILD_ID || 'local build';
// Label every non-production project (e.g. aldg-contributions-dev) so it's never mistaken for prod.
const ENVIRONMENT_LABEL = /-dev$/.test(config.projectId || '') ? 'DEV' : '';

const rootElement = document.getElementById('root');

const renderApp = () => {
  const configClass = `${
    config.projectId.indexOf('-dev') > -1 ? 'bg-dev' : ''
  } ${config.projectId.indexOf('-training') > -1 ? 'bg-training' : ''}`.trim();
  try {
    ReactDOM.render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={AldgTheme}>
          <RecoilRoot>
            <OfflineProvider>
              <UserProvider>
                <ErrorBoundary
                  componentName='Main Application'
                  user={{ email: '' }}
                >
                  <BrowserRouter>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <ToastContainer
                        position='bottom-left'
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                      />
                      <Suspense
                        fallback={
                          <div className='loader-container'>
                            <div className='loader-container-inner'>
                              <div className='text-center'>
                                <Loader type='TailSpin' />
                              </div>
                              <h6 className='mt-3'>
                                Please wait while we load the Application
                              </h6>
                            </div>
                          </div>
                        }
                      >
                        <div
                          id='appParent'
                          className={configClass}
                          style={{
                            display: 'flex',
                            height: '100%',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'center'
                          }}
                        >
                          {/* <ChangelogModal /> */}
                          <Header />
                          <Application />
                          {/* BUILD_ID (see below) names the exact build deployed, in the drawer's
                              footer and in the always-visible corner tag. QA's version check reads it. */}
                          <Footer
                            version={BUILD_ID}
                            copy={`${new Date().getFullYear()} Aldridge, All Rights Reserved`}
                          />
                          <VersionTag version={BUILD_ID} environment={ENVIRONMENT_LABEL} />
                          <ServiceWorkerWrapper />
                        </div>
                      </Suspense>
                    </LocalizationProvider>
                  </BrowserRouter>
                </ErrorBoundary>
              </UserProvider>
            </OfflineProvider>
          </RecoilRoot>
        </ThemeProvider>
      </StyledEngineProvider>,
      rootElement
    );
  } catch (err) {
    // eslint-disable-next-line
  }
};
renderApp();
// serviceWorkerRegistration.register();
